import { Controller, useForm, useFormState } from "react-hook-form";
import Button, { ButtonProps } from "@vfuk/core-button";
import { FloatingLabelInput, Stack, useDebounce } from "@ucchub/ui";
import Container from "@vfuk/core-container";
import { Grid, GridRow, GridColumn } from "@vfuk/core-grid";
import Spacing from "@vfuk/core-spacing";
import { t } from "i18next";
import { useEffect, useState } from "react";
import CustomerFormHeader from "./CustomerFormHeader";
import CustomerDiscardPopup from "./CustomerDiscardPopup";
import useCustomerDetails from "@hooks/Customers/useCustomerCreateEdit";
import Paragraph from "@vfuk/core-paragraph";
import { StatusTogglePaddingStyles } from "./CustomerFormStyles";
import { Toggle } from "@vfuk/core-toggle";
import { Heading } from "@vfuk/core-heading";
import CustomerUpdatePopup from "./CustomerUpdatePopup";
import { getCustomerRules } from "./CustomerRules";
import { CustomerFormProps } from "@interfaces/customer";
import { useNavigate } from "react-router-dom";

const EditCustomerForm = ({
  isEdit,
  customerId,
  customer,
  products,
}: CustomerFormProps) => {
  const formHandler = useForm({ mode: "onTouched", shouldFocusError: false });
  const { control } = formHandler;
  const { isCustomerNameExist, updateCustomer } = useCustomerDetails();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const { handleSubmit, formState, setValue, reset } = formHandler;
  const { dirtyFields } = useFormState({ control });
  const { errors, isValid, isDirty } = formState;
  const [disableBtn, setDisableBtn] =
    useState<ButtonProps["state"]>("disabled");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [customerName, setCustomerName] = useState<string>();
  const [searchCustomerNameQuery, setSearchCustomerNameQuery] = useState("");
  const [isNameAlreadyExist, setIsNameAlreadyExist] = useState(false);
  const debouncedSearchCustomerNameTerm = useDebounce<string>(
    searchCustomerNameQuery,
    500
  );
  const navigate = useNavigate();
  useEffect(() => {
    if (debouncedSearchCustomerNameTerm) {
      isCustomerNameExist(debouncedSearchCustomerNameTerm).then(
        (isNameExist: boolean) => {
          setIsNameAlreadyExist(isNameExist);
        }
      );
    } else {
      setIsNameAlreadyExist(false);
    }
  }, [debouncedSearchCustomerNameTerm]);
  const onUpdate = handleSubmit((data) => {
    data.status = data.status === true ? "Inactive" : "Active";
    updateCustomer(data, customerId, true, false, customer).then((response) => {
      if (response.status === "success") {
        setShowUpdateModal(true);
      }
    });
    setCustomerName(data.name);
  });
  useEffect(() => {
    if (isDirty) {
      if (
        Object.keys(dirtyFields).length !== 0 &&
        Object.keys(formState.errors).length === 0 &&
        !isNameAlreadyExist
      ) {
        setDisableBtn(undefined);
      } else setDisableBtn("disabled");
    }
  }, [isDirty, isValid, formState]);
  useEffect(() => {
    reset({
      ...customer,
      ...{ status: customer?.status === "Inactive" ? true : false },
    });
  }, [customer, isEdit, setValue]);
  const customerRules = getCustomerRules();
  return (
    <>
      {showUpdateModal && (
        <CustomerUpdatePopup
          showModal={() => setShowDiscardModal(false)}
          customerName={customerName}
          customerId={customerId}
        />
      )}
      {showDiscardModal && (
        <CustomerDiscardPopup
          isOpen={showDiscardModal}
          onCancel={() => {
            setShowDiscardModal(false);
          }}
          onConfirm={() => {
            navigate(`/customers/${customerId + "/view"}`);
          }}
        />
      )}
      <Container appearance="secondary">
        <>
          <form onSubmit={onUpdate}>
            <Spacing spacingLevel={{ top: 4 }}>
              <Container elevation>
                <CustomerFormHeader />
                <Grid>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="name"
                        control={control}
                        rules={customerRules.customerName}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:customer_name")}
                            error={
                              isNameAlreadyExist
                                ? t("core:customers:customer_exists") ||
                                  undefined
                                : errors.name
                                ? (errors.name?.message as string) || undefined
                                : undefined
                            }
                            type="text"
                            id="name"
                            state={
                              errors.name || isNameAlreadyExist
                                ? "error"
                                : undefined
                            }
                            {...field}
                            onChange={(e) => {
                              setSearchCustomerNameQuery(e.target.value.trim());
                              field.onChange(e.target.value);
                            }}
                          />
                        )}
                      />
                    </GridColumn>

                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Stack
                        direction="column"
                        justifyContent="left"
                        align="flex-start"
                      >
                        <Paragraph noMargin>
                          {t("core:customers:label:status_heading")}
                        </Paragraph>

                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Spacing spacingLevel={{ top: 2 }}>
                              <StatusTogglePaddingStyles>
                                <Toggle
                                  id="status"
                                  align="left"
                                  isActive={field.value}
                                  showIcons={false}
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                  }}
                                  state={
                                    products?.length !== 0
                                      ? undefined
                                      : "disabled"
                                  }
                                >
                                  Inactive
                                </Toggle>
                              </StatusTogglePaddingStyles>
                            </Spacing>
                          )}
                        />
                      </Stack>
                    </GridColumn>
                  </GridRow>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="addressLine1"
                        control={control}
                        rules={customerRules.addressLine1}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:address_line_1")}
                            error={
                              errors.addressLine1
                                ? (errors.addressLine1.message as string)
                                : undefined
                            }
                            type="text"
                            id="addressLine1"
                            state={errors.addressLine1 ? "error" : undefined}
                            {...field}
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="addressLine2"
                        control={control}
                        rules={customerRules.addressLine2}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:address_line_2")}
                            error={
                              errors.addressLine2
                                ? (errors.addressLine2.message as string)
                                : undefined
                            }
                            type="text"
                            id="addressLine2"
                            {...field}
                            state={errors.addressLine2 ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="country"
                        control={control}
                        rules={customerRules.country}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:country")}
                            type="text"
                            id="country"
                            error={
                              errors.country
                                ? (errors.country.message as string)
                                : undefined
                            }
                            {...field}
                            state={errors.country ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="city"
                        control={control}
                        rules={customerRules.city}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:city")}
                            error={
                              errors.city
                                ? (errors.city.message as string)
                                : undefined
                            }
                            type="text"
                            id="city"
                            {...field}
                            state={errors.city ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="area"
                        control={control}
                        rules={customerRules.area}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:area")}
                            error={
                              errors.area
                                ? (errors.area.message as string)
                                : undefined
                            }
                            type="text"
                            id="area"
                            {...field}
                            state={errors.area ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="postcode"
                        control={control}
                        rules={customerRules.postcode}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:post_code")}
                            error={
                              errors.postcode
                                ? (errors.postcode.message as string)
                                : undefined
                            }
                            type="text"
                            id="postcode"
                            {...field}
                            state={errors.postcode ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        rules={customerRules.phoneNumber}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:phone_number")}
                            error={
                              errors.phoneNumber
                                ? (errors.phoneNumber.message as string)
                                : undefined
                            }
                            type="text"
                            id="phoneNumber"
                            {...field}
                            state={errors.phoneNumber ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="contactFirstName"
                        control={control}
                        rules={customerRules.firstName}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:contact_first_name")}
                            error={
                              errors.contactFirstName
                                ? (errors.contactFirstName.message as string)
                                : undefined
                            }
                            type="text"
                            id="contactFirstName"
                            {...field}
                            state={
                              errors.contactFirstName ? "error" : undefined
                            }
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="contactLastName"
                        control={control}
                        rules={customerRules.lastName}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:contact_last_name")}
                            error={
                              errors.contactLastName
                                ? (errors.contactLastName.message as string)
                                : undefined
                            }
                            type="text"
                            id="contactLastName"
                            {...field}
                            state={errors.contactLastName ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Controller
                        name="contactEmail"
                        control={control}
                        rules={customerRules.email}
                        render={({ field }) => (
                          <FloatingLabelInput
                            label={t("core:customers:label:contact_email")}
                            error={
                              errors.contactEmail
                                ? (errors.contactEmail.message as string)
                                : undefined
                            }
                            type="text"
                            id="contactEmail"
                            {...field}
                            state={errors.contactEmail ? "error" : undefined}
                          />
                        )}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow marginTop={3} marginBottom={3}>
                    <GridColumn col={12} colMd={4} colLg={4}>
                      <Heading level={5} weight={3}>
                        {t("core:customers:label:pid")}
                      </Heading>
                      <span>{customer?.pid ? customer?.pid : "NA"} </span>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Container>
            </Spacing>
            <Spacing spacingLevel={{ top: 4 }}>
              <Stack justifyContent="right">
                <Stack>
                  <Spacing spacingLevel={{ right: 2 }}>
                    <Button
                      text={t("core:customers:cancel")}
                      onClick={() => {
                        if (Object.keys(dirtyFields).length !== 0) {
                          setShowDiscardModal(true);
                        } else {
                          navigate("/customers/" + customerId + "/view");
                        }
                      }}
                      appearance="alt1"
                    />
                  </Spacing>
                  <Button
                    onClick={onUpdate} // Empty `onClick` adds the 'pointer' cursor
                    text={t("core:customers:save_changes")}
                    appearance={disableBtn ? "secondary" : "primary"}
                    state={disableBtn || undefined}
                  />
                </Stack>
              </Stack>
            </Spacing>
          </form>
        </>
      </Container>
    </>
  );
};
export default EditCustomerForm;
